/* eslint-disable @typescript-eslint/naming-convention */
import { AxiosResponse, AxiosError } from "axios";
import request from "#/utils/request";
import InternalResponse from "#/types/internalResponse";
import { authHeaders } from "#/modules/auth/idp";

// One role assignment on a user: role + scope (scope_type SYSTEM|CUSTOMER|LICENSE, scope_id).
export type UserAssignment = {
  role: string;
  scope_type: string;
  scope_id: string;
  status: string;
};

export type UserItem = {
  user_id: string;
  username: string;
  email: string;
  status: string;
  assignments: UserAssignment[];
};

type ListUsersResponse = { items: UserItem[] };

export type CreateUserBody = {
  username: string;
  email: string;
  password: string;
  role: string;
  scope_type: string;
  scope_id: string;
  send_invite?: boolean;
};

// scope_type SYSTEM|CUSTOMER|LICENSE; scope_id = customer_id / license_id / "SYSTEM".
export type AssignmentBody = { role: string; scope_type: string; scope_id: string };

const BASE = "/xecguard/internal/v1/users";

const ok = <T>(result: AxiosResponse<T>): InternalResponse<T> => ({
  statusCode: result.status,
  data: result.data,
  success: true,
});

const fail = (error: AxiosError): InternalResponse => ({
  statusCode: error.response?.status ?? error.code,
  msg: error.message,
  success: false,
});

// GET /users — users visible to the caller (auto role-scoped by the backend). The optional
// customer_id / license_id narrow the view to a Territory / Organization (for SYSTEM_ADMIN this
// is a pure in-page filter — it does not change the active X-License-Id).
export function listUsers(
  { token, customerId, licenseId }: { token: string; customerId?: string; licenseId?: string },
): Promise<InternalResponse<ListUsersResponse>> {
  const params: Record<string, string> = {};
  if (customerId) params.customer_id = customerId;
  if (licenseId) params.license_id = licenseId;
  return request()<ListUsersResponse>({
    url: BASE, method: "GET", headers: authHeaders(token), params,
  }).then(ok).catch(fail);
}

// POST /users — create a user with an initial role + scope (backend enforces RBAC limits).
export function createUser(
  { token, body }: { token: string; body: CreateUserBody },
): Promise<InternalResponse> {
  return request()({
    url: BASE, method: "POST", headers: authHeaders(token), data: body,
  })
    .then(ok).catch(fail);
}

// DELETE /users/{id} — soft-disable a user (SYSTEM_ADMIN; cannot disable own account).
export function disableUser(
  { token, userId }: { token: string; userId: string },
): Promise<InternalResponse> {
  return request()({ url: `${BASE}/${userId}`, method: "DELETE", headers: authHeaders(token) })
    .then(ok).catch(fail);
}

// POST /users/{id}/assignments — add a role assignment.
export function addAssignment(
  { token, userId, body }: { token: string; userId: string; body: AssignmentBody },
): Promise<InternalResponse> {
  return request()({
    url: `${BASE}/${userId}/assignments`, method: "POST", headers: authHeaders(token), data: body,
  }).then(ok).catch(fail);
}

// DELETE /users/{id}/assignments — remove a role assignment (cannot remove own).
export function removeAssignment(
  { token, userId, body }: { token: string; userId: string; body: AssignmentBody },
): Promise<InternalResponse> {
  return request()({
    url: `${BASE}/${userId}/assignments`, method: "DELETE", headers: authHeaders(token), data: body,
  }).then(ok).catch(fail);
}
